import * as XLSX from 'xlsx';

const BACKUP_VERSION = '2.0';
const SHEETS = {
    summary: 'Сводка',
    categories: 'Категории',
    transactions: 'Операции',
    legacyTransactions: 'Транзакции',
    goals: 'Цели',
    notes: 'Заметки'
};

const TYPE_LABELS = {
    expenses: 'Расход',
    income: 'Доход',
    goals: 'Цель'
};

const TYPE_BY_LABEL = {
    Расход: 'expenses',
    Расходы: 'expenses',
    expenses: 'expenses',
    Доход: 'income',
    Доходы: 'income',
    income: 'income',
    Цель: 'goals',
    Цели: 'goals',
    goals: 'goals'
};

const getToday = () => new Date().toISOString().split('T')[0];
const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const asString = (value) => value == null ? '' : String(value).trim();
const asNumber = (value) => {
    const normalized = typeof value === 'string' ? value.replace(',', '.').trim() : value;
    const number = Number(normalized);
    return Number.isFinite(number) ? number : 0;
};

const formatDateValue = (value) => {
    if (!value) return getToday();
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().split('T')[0];
    }

    const text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
    }

    return getToday();
};

const formatDateTimeValue = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 16);
    }
    return String(value).trim() || null;
};

const categoryNameMaps = (categories) => ({
    expenses: Object.fromEntries(categories.expenses.map(category => [category.name, category.id])),
    income: Object.fromEntries(categories.income.map(category => [category.name, category.id]))
});

const getSheetRows = (workbook, sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    return sheet ? XLSX.utils.sheet_to_json(sheet, { defval: '' }) : [];
};

const buildSummaryRows = ({ categories, transactions, notes }) => {
    const totalExpenses = transactions.expenses.reduce((sum, transaction) => sum + transaction.amount, 0);
    const totalIncome = transactions.income.reduce((sum, transaction) => sum + transaction.amount, 0);

    return [
        { Поле: 'Версия формата', Значение: BACKUP_VERSION },
        { Поле: 'Дата экспорта', Значение: new Date().toLocaleString('ru-RU') },
        { Поле: 'Категорий расходов', Значение: categories.expenses.length },
        { Поле: 'Категорий доходов', Значение: categories.income.length },
        { Поле: 'Целей', Значение: categories.goals.length },
        { Поле: 'Операций расходов', Значение: transactions.expenses.length },
        { Поле: 'Операций доходов', Значение: transactions.income.length },
        { Поле: 'Заметок', Значение: notes.length },
        { Поле: 'Всего расходов', Значение: totalExpenses },
        { Поле: 'Всего доходов', Значение: totalIncome },
        { Поле: 'Баланс', Значение: totalIncome - totalExpenses }
    ];
};

const buildCategoryRows = (categories) => [
    ...categories.expenses.map(category => ({
        ID: category.id,
        Тип: TYPE_LABELS.expenses,
        Название: category.name,
        Лимит: category.limit || 0,
        Комментарий: 'Категория для расходов'
    })),
    ...categories.income.map(category => ({
        ID: category.id,
        Тип: TYPE_LABELS.income,
        Название: category.name,
        Лимит: '',
        Комментарий: 'Категория для доходов'
    }))
];

const buildTransactionRows = ({ categories, transactions }) => [
    ...transactions.expenses.map(transaction => {
        const category = categories.expenses.find(item => String(item.id) === String(transaction.categoryId));
        const date = formatDateValue(transaction.date);
        return {
            ID: transaction.id,
            Тип: TYPE_LABELS.expenses,
            Дата: date,
            Год: Number(date.slice(0, 4)),
            Месяц: date.slice(0, 7),
            'Категория ID': transaction.categoryId,
            Категория: category?.name || '',
            Сумма: transaction.amount,
            Комментарий: ''
        };
    }),
    ...transactions.income.map(transaction => {
        const category = categories.income.find(item => String(item.id) === String(transaction.categoryId));
        const date = formatDateValue(transaction.date);
        return {
            ID: transaction.id,
            Тип: TYPE_LABELS.income,
            Дата: date,
            Год: Number(date.slice(0, 4)),
            Месяц: date.slice(0, 7),
            'Категория ID': transaction.categoryId,
            Категория: category?.name || '',
            Сумма: transaction.amount,
            Комментарий: ''
        };
    })
];

const buildGoalRows = (goals) => goals.map(goal => {
    const target = goal.target || 0;
    const current = goal.current || 0;
    return {
        ID: goal.id,
        Название: goal.name,
        Цель: target,
        Накоплено: current,
        Осталось: Math.max(0, target - current),
        'Прогресс %': target > 0 ? Math.round((current / target) * 100) : 0
    };
});

const buildNoteRows = (notes) => notes.map(note => ({
    ID: note.id,
    Текст: note.text,
    Дата: formatDateValue(note.date),
    Напоминание: note.reminder || '',
    Топ: note.isTop ? 'Да' : 'Нет'
}));

const appendJsonSheet = (workbook, rows, name) => {
    const sheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, sheet, name);
};

export const downloadFinanceBackup = ({ categories, transactions, notes }) => {
    const workbook = XLSX.utils.book_new();
    appendJsonSheet(workbook, buildSummaryRows({ categories, transactions, notes }), SHEETS.summary);
    appendJsonSheet(workbook, buildCategoryRows(categories), SHEETS.categories);
    appendJsonSheet(workbook, buildTransactionRows({ categories, transactions }), SHEETS.transactions);
    appendJsonSheet(workbook, buildGoalRows(categories.goals), SHEETS.goals);
    appendJsonSheet(workbook, buildNoteRows(notes), SHEETS.notes);
    XLSX.writeFile(workbook, `finance_backup_${getToday()}.xlsx`);
};

const parseCategories = (categoryRows, goalRows) => {
    const categories = { expenses: [], income: [], goals: [] };

    categoryRows.forEach((row) => {
        const type = TYPE_BY_LABEL[asString(row.Тип)] || TYPE_BY_LABEL[asString(row.Type)];
        const name = asString(row.Название || row.Name);
        if (!type || !name) return;

        if (type === 'expenses') {
            categories.expenses.push({
                id: asString(row.ID) || makeId(),
                name,
                limit: asNumber(row.Лимит || row.Limit)
            });
        }

        if (type === 'income') {
            categories.income.push({
                id: asString(row.ID) || makeId(),
                name
            });
        }

        if (type === 'goals') {
            categories.goals.push({
                id: asString(row.ID) || makeId(),
                name,
                target: asNumber(row.Цель || row.Target),
                current: asNumber(row.Накоплено || row.Current)
            });
        }
    });

    goalRows.forEach((row) => {
        const name = asString(row.Название || row.Name);
        if (!name) return;

        categories.goals.push({
            id: asString(row.ID) || makeId(),
            name,
            target: asNumber(row.Цель || row.Target),
            current: asNumber(row.Накоплено || row.Current)
        });
    });

    return categories;
};

const parseTransactions = (transactionRows, categories) => {
    const transactions = { expenses: [], income: [], savings: [] };
    const nameToId = categoryNameMaps(categories);

    transactionRows.forEach((row) => {
        const type = TYPE_BY_LABEL[asString(row.Тип)] || TYPE_BY_LABEL[asString(row.Type)];
        const amount = asNumber(row.Сумма || row.Amount);
        if ((type !== 'expenses' && type !== 'income') || amount <= 0) return;

        const categoryName = asString(row.Категория || row.Category);
        const categoryId = asString(row['Категория ID'] || row.CategoryId || row.categoryId)
            || nameToId[type][categoryName]
            || '';

        transactions[type].push({
            id: asString(row.ID) || makeId(),
            categoryId,
            amount,
            date: formatDateValue(row.Дата || row.Date)
        });
    });

    return transactions;
};

const parseNotes = (noteRows) => noteRows
    .map((row) => ({
        id: asString(row.ID) || makeId(),
        text: asString(row.Текст || row.Text),
        date: formatDateValue(row.Дата || row.Date),
        reminder: formatDateTimeValue(row.Напоминание || row.Reminder),
        isTop: asString(row.Топ || row.Top).toLowerCase() === 'да'
            || asString(row.Топ || row.Top).toLowerCase() === 'true'
    }))
    .filter(note => note.text);

export const parseFinanceBackup = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });

    const categoryRows = getSheetRows(workbook, SHEETS.categories);
    const goalRows = getSheetRows(workbook, SHEETS.goals);
    const transactionRows = getSheetRows(workbook, SHEETS.transactions).length
        ? getSheetRows(workbook, SHEETS.transactions)
        : getSheetRows(workbook, SHEETS.legacyTransactions);
    const noteRows = getSheetRows(workbook, SHEETS.notes);

    const categories = parseCategories(categoryRows, goalRows);
    const transactions = parseTransactions(transactionRows, categories);
    const notes = parseNotes(noteRows);

    return { categories, transactions, notes };
};
